import { Link } from "react-router-dom";
import {
  Wine, Layers, Boxes, Lock,
  Check, Mail, LineChart,
} from "lucide-react";
import cdiLogo from "@/assets/cdi-logo.png";

/**
 * LandingPage — public marketing page served at `/` for unauthenticated
 * visitors. Styled with the app's shared Tailwind + HSL design tokens
 * (`vp-card`, `vp-btn`, `vp-muted`, `--gold`, `--bg`, `--bg-elevated`,
 * `--border`) so the look is identical to the authenticated shell.
 */
export default function LandingPage() {
  return (
    <div
      className="min-h-screen bg-[hsl(var(--bg))] text-[hsl(var(--fg))]"
      data-testid="landing-page"
    >
      {/* SECTION 1 — HEADER */}
      <header
        className="fixed top-0 inset-x-0 z-30 border-b border-[hsl(var(--border))] bg-[hsl(var(--bg))]/95 backdrop-blur-sm"
        data-testid="landing-header"
      >
        <div className="max-w-6xl mx-auto flex items-center justify-between px-4 sm:px-6 py-6">
          <Link
            to="/"
            className="flex items-center gap-[13px]"
            data-testid="landing-header-brand"
          >
            <img
              src={cdiLogo}
              alt="CDI"
              data-testid="landing-header-brand-logo-img"
              className="block shrink-0"
            />
            <span className="text-base sm:text-lg font-bold tracking-tight text-[hsl(var(--gold))]">
              Collectables
            </span>
          </Link>
          <nav className="flex items-center gap-3">
            <Link
              to="/auth/signin"
              className="vp-btn vp-btn-secondary px-5 py-2.5"
              data-testid="landing-header-signin"
            >
              Sign In
            </Link>
            <Link
              to="/auth/signup"
              className="vp-btn vp-btn-primary px-5 py-2.5"
              data-testid="landing-header-signup"
            >
              Start Free Trial
            </Link>
          </nav>
        </div>
      </header>

      <main className="pt-24">
        {/* SECTION 2 — HERO */}
        <section
          className="max-w-6xl mx-auto px-4 sm:px-6 pt-16 pb-20 grid lg:grid-cols-2 gap-10 items-center"
          data-testid="landing-hero"
        >
          <div className="max-w-xl">
            <h1 className="text-base sm:text-lg font-normal tracking-tight">
              CDI brings your whisky, wine, and spirits collections together in a way that's clean, simple, and quietly premium.
            </h1>
            <p className="mt-2 text-base sm:text-lg font-normal tracking-tight vp-muted">
              Created by a collector who wanted one clear place to organise and understand their collection.
            </p>
            <blockquote className="mt-6 border-l-2 border-[hsl(var(--gold))] pl-4 text-lg sm:text-xl italic vp-muted max-w-prose">
              I've collected whisky, wine, and spirits for years, and I always wanted one place where I could see everything clearly — what I owned, what it was worth, and how my collection was performing. Nothing out there ever felt simple or complete. So I built CDI: a clean, modern way to track your collections, understand their value, and enjoy the experience of keeping everything together.
            </blockquote>
            <div className="mt-10 flex flex-wrap items-center gap-3">
              <Link
                to="/auth/signup"
                className="vp-btn vp-btn-primary"
                data-testid="landing-hero-signup"
              >
                Start Free Trial
              </Link>
              <Link
                to="/auth/signin"
                className="vp-btn vp-btn-secondary"
                data-testid="landing-hero-signin"
              >
                Sign In
              </Link>
            </div>
          </div>
          <div
            className="flex flex-col gap-3 mt-10 lg:mt-0"
            data-testid="landing-hero-cards"
          >
            {[
              {
                Icon: Boxes,
                title: "Collections",
                body:
                  "Organise whisky, wine, and spirits into clean, structured collections. Unlimited collections. Unlimited items.",
                testid: "landing-hero-card-collections",
              },
              {
                Icon: Layers,
                title: "Categories",
                body:
                  "Preset category fields, with the option to add custom fields to personalise your experience.",
                testid: "landing-hero-card-categories",
              },
              {
                Icon: Wine,
                title: "Item Management",
                body:
                  "Add detailed item information including identity fields, tasting notes, storage location, purchase details, and photos.",
                testid: "landing-hero-card-items",
              },
              {
                Icon: LineChart,
                title: "Analytics & History",
                body:
                  "Track collection value, record sales, monitor changes over time, and understand how your collection is performing.",
                testid: "landing-hero-card-analytics",
              },
            ].map((c) => (
              <div
                key={c.title}
                className="vp-card p-6"
                data-testid={c.testid}
              >
                <div className="flex items-center gap-2 mb-2">
                  <c.Icon size={22} className="text-[hsl(var(--gold))]" />
                  <h3 className="font-semibold text-base">{c.title}</h3>
                </div>
                <p className="text-sm vp-muted leading-relaxed">{c.body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* SECTION 5 — HOW IT WORKS (left) + BASIC PLAN (right) */}
        <section
          className="max-w-6xl mx-auto px-4 sm:px-6 pt-0 pb-16 grid lg:grid-cols-2 gap-8 items-start"
          data-testid="landing-plans-block"
        >
          {/* How It Works — left column */}
          <div
            className="vp-card p-6"
            data-testid="landing-how-it-works"
          >
            <h2 className="text-base sm:text-lg font-bold tracking-tight mb-5">
              How it works
            </h2>
            <ol className="flex flex-col gap-5">
              {[
                {
                  step: "1",
                  title: "Create your account",
                  body: "Start with a free 3-day trial with limited access. No credit card required.",
                },
                {
                  step: "2",
                  title: "Build your collections",
                  body: "Add items, categories, photos, and notes to try the Basic features.",
                },
                {
                  step: "3",
                  title: "Upgrade to Basic",
                  body: "At any time, purchase the Basic plan for permanent access.",
                },
              ].map((s) => (
                <li
                  key={s.step}
                  className="flex items-start gap-4"
                  data-testid={`landing-step-${s.step}`}
                >
                  <div className="h-9 w-9 rounded-full border-2 border-[hsl(var(--gold))] flex items-center justify-center font-black text-[hsl(var(--gold))] flex-shrink-0">
                    {s.step}
                  </div>
                  <div>
                    <h3 className="text-sm sm:text-base font-bold tracking-tight mb-1">{s.title}</h3>
                    <p className="text-sm font-light vp-muted leading-normal">{s.body}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>

          {/* Basic Plan — right column */}
          <div
            className="vp-card p-6 shadow-xl ring-1 ring-[hsl(var(--gold))]/30"
            data-testid="landing-pricing"
          >
            <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
              <div>
                <h2 className="text-base sm:text-lg font-bold tracking-tight mb-2">
                  Basic Plan
                </h2>
                <p className="text-sm font-light vp-muted leading-normal">
                  One-time payment
                </p>
                <p className="text-sm font-light vp-muted mt-1 leading-normal">
                  Permanent access to all Basic features, including:
                </p>
              </div>
              <div className="text-right">
                <div className="text-5xl font-medium text-[hsl(var(--gold))]">
                  $35
                </div>
                <div className="text-xs vp-muted">NZD — one-time</div>
              </div>
            </div>
            <ul className="mt-4 grid sm:grid-cols-2 gap-y-1 gap-x-2 text-sm font-light leading-normal">
              {[
                "Unlimited Collections",
                "Unlimited Items",
                "Unlimited Categories",
                "Photos",
                "Notes",
                "Portfolio Analytics",
                "Basic Analytics",
                "Limited Whisky & Spirits Analytics",
                "Dashboard",
              ].map((f) => (
                <li key={f} className="flex items-center gap-2">
                  <Check size={16} className="text-[hsl(var(--gold))] flex-shrink-0" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
            <Link
              to="/auth/signup"
              className="vp-btn vp-btn-primary mt-6 w-full justify-center"
              data-testid="landing-pricing-cta"
            >
              Start Free Trial
            </Link>
          </div>
        </section>

        {/* SECTION 6 — PREMIUM PLAN (full width) */}
        <section
          className="max-w-6xl mx-auto px-4 sm:px-6 pb-16"
          data-testid="landing-premium"
        >
          <div className="vp-card p-6 border-dashed">
            <div className="flex items-center gap-2 mb-3">
              <Lock size={16} className="text-[hsl(var(--gold))]" />
              <h2 className="text-base sm:text-lg font-bold tracking-tight">
                Premium Plan
              </h2>
            </div>
            <p className="text-sm font-light vp-muted leading-normal">
              Advanced features arriving soon, starting with:
            </p>
            <ul className="mt-2 grid sm:grid-cols-2 gap-y-1 gap-x-2 text-sm font-light leading-normal">
              {[
                "Import",
                "Export",
                "Advanced whisky analytics",
                "Advanced spirits analytics",
              ].map((f) => (
                <li key={f} className="flex items-center gap-2">
                  <Check size={16} className="text-[hsl(var(--gold))] flex-shrink-0" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
            <p className="mt-3 text-sm font-light vp-muted leading-normal">
              Stay tuned — Premium adds more ways to refine and manage your collection with ease.
            </p>
          </div>
        </section>

        {/* SECTION 7 — ELIGIBILITY (full-width card) */}
        <section
          className="max-w-6xl mx-auto px-4 sm:px-6 pb-20"
          data-testid="landing-compliance"
        >
          <div className="vp-card p-6 space-y-5">
            <div>
              <h3 className="text-base sm:text-lg font-bold tracking-tight mb-1.5">
                Eligibility
              </h3>
              <p className="text-sm font-light vp-muted leading-normal">
                CDI Collectables is available to users who: are 18 years or older, are not located in, and not ordinarily resident in, the United Kingdom or the European Union.
              </p>
            </div>
            <div>
              <h3 className="text-base sm:text-lg font-bold tracking-tight mb-1.5">
                Trial
              </h3>
              <p className="text-sm font-light vp-muted leading-normal">
                Includes a free 3-day trial. No charges occur during the trial.
              </p>
            </div>
            <div>
              <h3 className="text-base sm:text-lg font-bold tracking-tight mb-1.5">
                Privacy
              </h3>
              <p className="text-sm font-light vp-muted leading-normal">
                By creating an account, you agree to our{" "}
                <Link
                  to="/legal/terms"
                  className="text-[hsl(var(--gold))] underline"
                  data-testid="landing-compliance-terms"
                >
                  Terms of Use
                </Link>{" "}
                and{" "}
                <Link
                  to="/legal/privacy"
                  className="text-[hsl(var(--gold))] underline"
                  data-testid="landing-compliance-privacy"
                >
                  Privacy Policy
                </Link>
                .
              </p>
            </div>
          </div>
        </section>
      </main>

      {/* SECTION 9 — FOOTER */}
      <footer
        className="border-t border-[hsl(var(--border))] bg-[hsl(var(--bg-elevated))]"
        data-testid="landing-footer"
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 text-xs vp-muted">
          <div className="flex flex-wrap gap-x-4 gap-y-1.5 mb-3">
            <Link
              to="/legal/terms"
              className="hover:text-[hsl(var(--gold))]"
              data-testid="landing-footer-terms"
            >
              Terms of Use
            </Link>
            <Link
              to="/legal/privacy"
              className="hover:text-[hsl(var(--gold))]"
              data-testid="landing-footer-privacy"
            >
              Privacy Policy
            </Link>
            <Link
              to="/legal/refund"
              className="hover:text-[hsl(var(--gold))]"
              data-testid="landing-footer-refund"
            >
              Refund Policy
            </Link>
            <Link
              to="/legal/subscription"
              className="hover:text-[hsl(var(--gold))]"
              data-testid="landing-footer-subscription"
            >
              Subscription Terms
            </Link>
          </div>
          <div className="flex items-center gap-2 mb-1.5">
            <Mail size={12} />
            <a
              href="mailto:support@cdicollectables.co.nz"
              className="hover:text-[hsl(var(--gold))]"
              data-testid="landing-footer-contact"
            >
              support@cdicollectables.co.nz
            </a>
          </div>
          <p>© 2026 Calbydoneit Limited</p>
        </div>
      </footer>
    </div>
  );
}
